import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { useAuth } from '@/auth'
import { useStore } from '@/store'
import { ChangePasswordModal } from '@/auth/change-password-modal'
import { Moon, Sun, LogOut, Key, Settings } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { userService } from '@/services/user-service'

export function SettingsPage() {
  const { user, logout } = useAuth()
  const { theme, toggleTheme } = useStore()
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [available, setAvailable] = useState<number | null>(null)

  useEffect(() => {
    let mounted = true
    userService.getAvailableAnalytics().then((res) => {
      if (mounted) setAvailable(res?.availableAnalytics ?? null)
    }).catch(() => {
      if (mounted) setAvailable(null)
    })
    return () => { mounted = false }
  }, [])

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <Card className="relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-slate-400/10 to-transparent dark:from-slate-400/5 rounded-full -translate-y-8 translate-x-8" />
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              <CardTitle className="text-2xl font-bold tracking-tight flex items-center gap-2">
                <Settings className="h-6 w-6 text-slate-600 dark:text-slate-400" />
                Settings
              </CardTitle>
              <p className="text-muted-foreground">Account preferences and configuration</p>
            </div>
            <Badge variant="secondary" className="text-xs">
              Account
            </Badge>
          </div>
        </CardHeader>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Profile</CardTitle>
          <CardDescription>Your account information</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Username</Label>
            <p className="text-sm text-muted-foreground">{user?.username}</p>
          </div>
          <div>
            <Label>Email</Label>
            <p className="text-sm text-muted-foreground">{user?.email}</p>
          </div>
          <div>
            <Label>Available Analytics Requests</Label>
            <p className="text-sm text-muted-foreground">{available === null ? '—' : available}</p>
          </div>
          <Button
            onClick={() => setShowPasswordModal(true)}
            variant="outline"
            className="w-full"
          >
            <Key className="h-4 w-4 mr-2" />
            Change Password
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Preferences</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <Label>Dark Mode</Label>
              <p className="text-sm text-muted-foreground">Toggle theme</p>
            </div>
            <Button onClick={toggleTheme} variant="outline" size="icon">
              {theme === 'light' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Account Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <Button onClick={logout} variant="destructive" className="w-full">
            <LogOut className="h-4 w-4 mr-2" />
            Sign Out
          </Button>
        </CardContent>
      </Card>

      <ChangePasswordModal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
      />
    </div>
  )
} 